import React, { useRef, useState, useEffect } from 'react';
import { FONTS } from '../../styles/tokens';

interface DeployDropZoneProps {
  /** Human-readable byte cap, e.g. "Max 100 MB". */
  sizeHint: string;
  isDark: boolean;
  border: string;
  textPrimary: string;
  textSecondary: string;
  accent: string;
  onFileSelected: (file: File) => void;
}

/**
 * Zip file MIME types we accept in addition to the `.zip` extension check.
 * macOS Finder sometimes omits extension metadata on drag; the MIME type is
 * the reliable fallback.
 */
const ZIP_MIME_TYPES: ReadonlySet<string> = new Set([
  'application/zip',
  'application/x-zip',
  'application/x-zip-compressed',
  'application/octet-stream',
]);

/**
 * Large, discoverable drag-and-drop zone for the deploy form. Attaches
 * window-level listeners so the browser can't steal the drop event by
 * navigating to the file URL.
 */
export function DeployDropZone(props: DeployDropZoneProps): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [rejectReason, setRejectReason] = useState<string | null>(null);

  useEffect(() => attachWindowGuards(), []);

  const handleFile = (file: File | undefined): void => {
    if (!file) return;
    console.debug('[DropZone] file', { name: file.name, type: file.type, size: file.size });
    if (!isZip(file)) {
      setRejectReason(`Not a .zip file: ${file.name || 'unnamed'} (${file.type || 'unknown type'})`);
      return;
    }
    setRejectReason(null);
    props.onFileSelected(file);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div
        onDragEnter={e => { e.preventDefault(); setDragActive(true); }}
        onDragOver={e => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={e => {
          if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
          setDragActive(false);
        }}
        onDrop={e => {
          e.preventDefault();
          setDragActive(false);
          handleFile(e.dataTransfer.files[0]);
        }}
        onClick={() => inputRef.current?.click()}
        style={zoneStyle(props, dragActive)}
      >
        <UploadIcon color={dragActive ? props.accent : props.textSecondary} />
        <div style={headingStyle(props.textPrimary)}>Drop your .zip here</div>
        <div style={bodyStyle(props.textSecondary)}>or click to browse</div>
        <div style={hintStyle(props.textSecondary)}>
          {props.sizeHint} · must contain a package.json, requirements.txt, go.mod, or Dockerfile
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".zip,application/zip,application/x-zip,application/x-zip-compressed"
          style={{ display: 'none' }}
          onChange={e => handleFile(e.target.files?.[0])}
        />
      </div>
      {rejectReason && (
        <div style={{ fontFamily: FONTS.body, fontSize: '12px', color: '#EF4444' }}>
          {rejectReason}
        </div>
      )}
    </div>
  );
}

/**
 * Attach window-level `dragover` + `drop` listeners that preventDefault
 * outside the drop zone. Without these, the browser navigates to the dropped
 * file URL and the drop zone's own handler never fires.
 */
function attachWindowGuards(): () => void {
  const prevent = (event: Event): void => { event.preventDefault(); };
  window.addEventListener('dragover', prevent);
  window.addEventListener('drop', prevent);
  return () => {
    window.removeEventListener('dragover', prevent);
    window.removeEventListener('drop', prevent);
  };
}

/** Check extension + MIME type — accept if either marker matches. */
function isZip(file: File): boolean {
  const lowerName = file.name.toLowerCase();
  if (lowerName.endsWith('.zip')) return true;
  if (file.type && ZIP_MIME_TYPES.has(file.type)) return true;
  return false;
}

/** Outer drop-zone container style. */
function zoneStyle(props: DeployDropZoneProps, dragActive: boolean): React.CSSProperties {
  return {
    padding: '48px 24px',
    borderRadius: '12px',
    border: `2px dashed ${dragActive ? props.accent : props.border}`,
    textAlign: 'center',
    backgroundColor: dragActive
      ? (props.isDark ? 'rgba(1,169,237,0.10)' : 'rgba(1,169,237,0.08)')
      : (props.isDark ? 'rgba(1,169,237,0.03)' : 'rgba(1,169,237,0.04)'),
    cursor: 'pointer',
    transition: 'border-color 0.15s, background-color 0.15s',
  };
}

/** Big heading inside the zone. */
function headingStyle(color: string): React.CSSProperties {
  return {
    fontFamily: FONTS.heading,
    fontSize: '14px',
    color,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    marginTop: '12px',
  };
}

/** Body text inside the zone. */
function bodyStyle(color: string): React.CSSProperties {
  return {
    fontFamily: FONTS.body,
    fontSize: '13px',
    color,
    marginTop: '6px',
  };
}

/** Small hint text below the zone heading. */
function hintStyle(color: string): React.CSSProperties {
  return {
    fontFamily: FONTS.body,
    fontSize: '11px',
    color,
    marginTop: '14px',
    opacity: 0.7,
  };
}

/** Simple cloud-upload SVG icon. */
function UploadIcon({ color }: { color: string }): React.JSX.Element {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', margin: '0 auto' }}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}
